import "pages/claims/AuditHistory.scss";

import Card from "components/Card/Card.tsx";
import { useLocation } from "react-router-dom";
import { useGetARScreeningAuditHistoryQuery } from "services/ar-screening-api";
import { useGetCostProjectionAuditHistoryQuery } from "services/cost-projection-api";
import { useGetMSAAuditHistoryQuery } from "services/msa-api";
import { useToken } from "utils/context.ts";
import { getTimeFromISOString } from "utils/date-utils";

import { nanoid } from "@reduxjs/toolkit";

type AuditHistoryDataType = {
  timestamp: string;
  action?: string;
  actor_username?: string;
  messages: string[];
};

const FutureMedicalAuditHistory = () => {
  const MSA = "msa";
  const LCP = "lcp";
  const MCP = "mcp";
  const AR_SCREENING = "ar-screening";
  const AMENDED_REVIEW = "amended-review";

  const location = useLocation();
  const pathSegments = location.pathname.split("/");

  const serviceType = pathSegments[2]; // "lcp" or "mcp"
  const fmsId = pathSegments[3];

  const { accessToken } = useToken();

  const {
    data: msaData,
    isLoading: msaLoading,
    error: msaError,
  } = useGetMSAAuditHistoryQuery(
    { id: fmsId },
    {
      skip:
        !(serviceType === MSA || serviceType === AMENDED_REVIEW) ||
        !accessToken,
    },
  );

  const {
    data: cpData,
    isLoading: cpLoading,
    error: cpError,
  } = useGetCostProjectionAuditHistoryQuery(
    { id: fmsId },
    {
      skip: !(serviceType === LCP || serviceType === MCP) || !accessToken,
    },
  );

  const {
    data: arData,
    isLoading: arLoading,
    error: arError,
  } = useGetARScreeningAuditHistoryQuery(
    { id: fmsId },
    { skip: !(serviceType === AR_SCREENING) || !accessToken },
  );

  // API returns a direct array, not { results: [...] }
  const auditHistoryMap: Record<string, AuditHistoryDataType[]> = {
    [MSA]: (msaData as unknown as AuditHistoryDataType[]) ?? [],
    [AMENDED_REVIEW]: (msaData as unknown as AuditHistoryDataType[]) ?? [],
    [AR_SCREENING]: (arData as unknown as AuditHistoryDataType[]) ?? [],
    [LCP]: (cpData as unknown as AuditHistoryDataType[]) ?? [],
    [MCP]: (cpData as unknown as AuditHistoryDataType[]) ?? [],
  };

  const auditHistory = auditHistoryMap[serviceType] ?? [];

  const isLoading =
    serviceType === LCP || serviceType === MCP
      ? cpLoading
      : serviceType === MSA || serviceType === AMENDED_REVIEW
        ? msaLoading
        : serviceType === AR_SCREENING
          ? arLoading
          : false;

  const error =
    serviceType === LCP || serviceType === MCP
      ? cpError
      : serviceType === MSA || serviceType === AMENDED_REVIEW
        ? msaError
        : serviceType === AR_SCREENING
          ? arError
          : null;

  return (
    <div className="swim-column">
      <Card heading="Audit History">
        {isLoading && (
          <div style={{ padding: "20px", textAlign: "center" }}>
            Loading audit history...
          </div>
        )}

        {error && (
          <div style={{ padding: "20px", color: "red" }}>
            <strong>Error loading audit history:</strong>
            <pre style={{ marginTop: "10px", fontSize: "12px" }}>
              {JSON.stringify(error, null, 2)}
            </pre>
          </div>
        )}

        {!isLoading && !error && auditHistory?.length === 0 && (
          <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
            No audit history found for this service.
          </div>
        )}

        {!isLoading &&
          !error &&
          auditHistory?.map((item) => {
            const formattedDate = getTimeFromISOString(
              item.timestamp,
              "dateTime",
            );
            return (
              <div key={nanoid()} className="AuditHistory">
                <label className="AuditHistory--date">{formattedDate}</label>
                <div className="AuditHistory--audit">
                  <div className="AuditHistory--audit--header">
                    {item.action !== undefined && (
                      <>
                        File was {item.action}d{" - "}
                      </>
                    )}
                    Change by {item.actor_username || "unknown"}
                  </div>
                  {item.messages.map((message) => (
                    <div key={nanoid()} className="AuditHistory--audit--item">
                      {message}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
      </Card>
    </div>
  );
};

export default FutureMedicalAuditHistory;
